import { defineMessages, t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { Container, Respond } from '@sm/wds-react';
import React, { useState } from 'react';
import cleanObject from '../../../../common/utils/cleanObject';
import { AppState } from '../../../handlers/handlers';
import navigateTo from '../../../helpers/navigateTo';
import getQueryParam, { hasQueryParam } from '../../../helpers/queryParams';
import SessionClient, { SessionResult } from '../../../helpers/sessionsApiClient';
import { removeIdToken } from '../../../helpers/sessionStorage';
import getAndValidateIdToken from './tokenHelper';
import { AuthState } from './types';
import './unlinked-page.scss';
import UnlinkedButtonOption from './UnlinkedButtonOption';
import UnlinkedErrorBanner from './UnlinkedErrorBanner';
import UnlinkedMessaging from './UnlinkedMessaging';

export const TEST_ID = 'UnlinkedPage';

const COPY = defineMessages({
  unlinkedPageSignupButtonTitle: {
    id: 'login.UnlinkedPageContent.SignupTitle',
    defaultMessage: 'Create a new account',
    description: '[Type: Button][Vis: high] - Button header for the user to create a new account',
  },
  unlinkedPageSignupButtonContent: {
    id: 'login.UnlinkedPageContent.SignupContent',
    defaultMessage: "Sign up for SurveyMonkey using your {socialName} account - it's free and easy",
    description:
      '[Type: Button][Vis: high] - Button text informing the user what happens if they click "Create a new account"',
  },
  unlinkedPageLoginButtonTitle: {
    id: 'login.UnlinkedPageContent.LoginTitle',
    defaultMessage: 'Log in to an existing account',
    description: '[Type: Button][Vis: high] - Button header for the user to log in to an existing account',
  },
  unlinkedPageLoginButtonContent: {
    id: 'login.UnlinkedPageContent.LoginContent',
    defaultMessage: 'Once you log in to SurveyMonkey, you can link your {socialName} account from the My Account page',
    description:
      '[Type: Button][Vis: high] - Button text informing the user what happens if they click "Log in to an existing acount"',
  },
});

export default function UnlinkedPageContent(): JSX.Element {
  const { idToken, socialName } = getAndValidateIdToken();

  const appState: AppState = {
    app: getQueryParam('sm.app'),
    ep: getQueryParam('ep'),
    forceAccountPicker: hasQueryParam('sm.force_account_picker') ? true : undefined,
    sm: getQueryParam('sm'),
    sm_allow_create_user: getQueryParam('sm_allow_create_user'),
    ut_source: getQueryParam('ut_source'),
    ut_source2: getQueryParam('ut_source2'),
  };

  // For the "Create a new account" button
  const [signupState, setSignupState] = useState<AuthState>('pageload');
  const [signupJWTError, setSignupJWTError] = useState<boolean>(false);

  const attemptSignUp = async (): Promise<void> => {
    if (signupState === 'pending' || signupState === 'success') {
      return;
    }
    setSignupState('pending');

    try {
      const result = await SessionClient().createBySignup({ claims: idToken, appState });
      if (result === SessionResult.CREATED) {
        setSignupState('success');
        removeIdToken();
        navigateTo('/login/next', cleanObject({ ep: appState.ep, joined: 'true' }));
      } else {
        setSignupState('error');
        setSignupJWTError(result === SessionResult.INVALID_JWT);
      }
    } catch (error: unknown) {
      setSignupJWTError(false);
      setSignupState('error');
    }
  };

  // For the "Log in to an existing acct" button
  const [loginState, setLoginState] = useState<AuthState>('pageload');

  const attemptLogin = (): void => {
    // navigation to /login handled by href attr; just set state here
    if (loginState !== 'pending' && loginState !== 'success') {
      setLoginState('pending');
    }
  };

  const generateLoginURL = (): string => {
    return createURL(
      '/login',
      cleanObject({
        'sm.app': appState.app,
        'sm.force_account_picker': appState.forceAccountPicker ? '' : undefined,
        ep: appState.ep,
        sm: appState.sm,
        sm_allow_create_user: appState.sm_allow_create_user,
        ut_source: appState.ut_source,
        ut_source2: appState.ut_source2,
      })
    );
  };

  const disallowCreateButton = (): boolean => {
    const baseUrl = window.location.href;
    return baseUrl.includes('eu.');
  };

  // For logic affecting either/both buttons
  const areButtonsDisabled: boolean =
    loginState === 'pending' || loginState === 'success' || signupState === 'pending' || signupState === 'success';

  return (
    <main data-testid={TEST_ID} className="sm-unlinked-page sm-unlinked-page__base">
      <Respond minWidth="md">
        {(matches: boolean) => (
          <Container flex flexJustify="center" flexDirection="column" className="sm-unlinked-page__content wds-p-t-7">
            <Container className="sm-unlinked-page__message-container" px={matches ? 0 : 3}>
              <UnlinkedMessaging socialName={socialName} />
            </Container>
            <If condition={signupState === 'error'}>
              <Container className="sm-unlinked-page__banner-container" px={matches ? 0 : 3}>
                <UnlinkedErrorBanner JWTError={signupJWTError} />
              </Container>
            </If>
            <Container
              className="sm-unlinked-page sm-unlinked-page__buttons-container wds-p-y-3"
              flex={matches}
              flexJustify="around"
            >
              <If condition={!disallowCreateButton()}>
                <UnlinkedButtonOption
                  isDisabled={areButtonsDisabled}
                  showSpinner={signupState === 'pending' || signupState === 'success'}
                  titleText={t(COPY.unlinkedPageSignupButtonTitle)}
                  buttonText={t(COPY.unlinkedPageSignupButtonContent, { socialName })}
                  onClick={attemptSignUp}
                  id="SIGNUP"
                />
              </If>
              <UnlinkedButtonOption
                isDisabled={areButtonsDisabled}
                showSpinner={loginState === 'pending' || loginState === 'success'}
                titleText={t(COPY.unlinkedPageLoginButtonTitle)}
                buttonText={t(COPY.unlinkedPageLoginButtonContent, { socialName })}
                onClick={attemptLogin}
                id="LOGIN"
                href={generateLoginURL()}
              />
            </Container>
          </Container>
        )}
      </Respond>
    </main>
  );
}
